// useFetchPut.js
import { useState } from "react";
import { uploadDiag, describeSignedUrl } from "../utils/uploadDiag";

const usePutData = () => {
   const [loading, setLoading] = useState(false);
   function normalizeFileName(name) {
      return name.normalize("NFKD").replace(/[^\x00-\x7F]/g, "");
   }

   const putData = async (url, method = "PUT", body = null, contentType, docOrigFileName, docSize, ocrType, uToken, uName, iconId) => {
      if (!navigator.onLine) {
         return { status: -2, message: "Network connection is lost" };
      }

      setLoading(true);
      const controller = new AbortController();
      const signal = controller.signal;

      const docsize = docSize.toString();
      const headers = {
         "Content-Type": contentType,
         "x-goog-meta-doc_orig_name": normalizeFileName(docOrigFileName),
         "x-goog-meta-doc_size": docsize,
         "x-goog-meta-ocr_type": ocrType,
         "x-goog-meta-user": uToken,
         "x-goog-meta-name": uName,
         "x-goog-meta-doc_type": contentType,
         "x-goog-meta-icon_src": iconId,
      };

      for (const [key, value] of Object.entries(headers)) {
         if (value === null || value === undefined || value === "") {
            console.error(`Upload blocked — missing or empty header value for: ${key}`);
            uploadDiag("put_blocked_missing_header", { file: docOrigFileName, header: key });
            return { st: -1, message: "Upload failed due to missing file metadata. Please try logging out and back in.", httpStatus: null };
         }
      }

      const requestOptions = {
         method,
         headers,
         body: body,
         signal,
      };

      console.log("REQUESTHEADER:", requestOptions.headers);

      const putStart = Date.now();
      uploadDiag("put_send", {
         file: docOrigFileName,
         sentHeaderKeys: Object.keys(headers),
         sentHeaderValues: headers,
         signedUrl: describeSignedUrl(url),
         online: navigator.onLine,
         origin: typeof window !== "undefined" ? window.location.origin : null,
      });

      try {
         const response = await fetch(url, requestOptions);

         if (response.ok && response.status === 200) {
            uploadDiag("put_ok", { file: docOrigFileName, status: response.status, elapsedMs: Date.now() - putStart });
            return { st: 1, message: "Successful" };
         } else {
            const errorText = await response.text();
            console.error(`Upload failed [HTTP ${response.status}]:`, errorText);
            // Capture GCS's response headers — these reveal whether it was a
            // signature mismatch, a CORS rejection, or which uploader handled it.
            const respHeaders = {};
            try {
               response.headers.forEach((v, k) => {
                  respHeaders[k] = v;
               });
            } catch {
               /* Headers may be opaque on some failures */
            }
            uploadDiag("put_fail", {
               file: docOrigFileName,
               status: response.status,
               elapsedMs: Date.now() - putStart,
               errorBody: errorText,
               responseHeaders: respHeaders,
            });
            return { st: -1, message: `Upload failed (${response.status}). Please try again or contact support.`, httpStatus: response.status };
         }
      } catch (error) {
         if (error.name === "AbortError") {
            uploadDiag("put_aborted", { file: docOrigFileName, elapsedMs: Date.now() - putStart });
            return { st: -2, message: "Upload cancelled" };
         } else if (!navigator.onLine) {
            uploadDiag("put_offline", { file: docOrigFileName, elapsedMs: Date.now() - putStart });
            return { st: -2, message: "Network connection lost. Please check your internet and try again." };
         } else {
            console.error("Upload request failed:", error);
            // A thrown fetch (e.g. "TypeError: Failed to fetch") with no HTTP
            // status typically means a CORS preflight rejection or a network/
            // mixed-content block — distinct from an HTTP 4xx from GCS above.
            uploadDiag("put_exception", {
               file: docOrigFileName,
               elapsedMs: Date.now() - putStart,
               errorName: error?.name,
               errorMessage: error?.message,
               online: navigator.onLine,
            });
            return { st: -2, message: "Unable to upload file. Please check your connection and try again." };
         }
      } finally {
         setLoading(false);
      }
   };

   return { putData, loading };
};

export default usePutData;
